import {
    LOC_COLOR,
    LOC_FLAGS,
    LOC_POSITION,
    LOC_POSITION_NEXT,
    LOC_POSITION_PREV,
    LOC_UV,
    LOC_UV_GRAD,
    VERTEX_FLAG_IS_BOUNDARY,
} from "#webgl2/webgl2-shader-constants"
import {
    ADDRESS_MODE_BORDER,
    ADDRESS_MODE_CLAMP_TO_EDGE,
    ADDRESS_MODE_MIRRORED_REPEAT,
    ADDRESS_MODE_REPEAT,
    ALLOW_WRAPPING_VIEWS,
    MAX_ATLAS_COUNT,
    MAX_MIP_LEVELS,
    MAX_TEXTURE_UNITS,
} from "#webgl2/constants"
import type {ShaderReturnType} from "@cm/hal"
import {WebGl2ImageAtlas} from "#webgl2/webgl2-image-atlas"

export class WebGl2ShaderCodeGen {
    static genVertexShader(args: {conservativeRasterization: boolean}): string {
        return `#version 300 es
            precision highp float;
            precision highp int;

            layout(location = ${LOC_POSITION}) in vec2 a_position;
            layout(location = ${LOC_UV}) in vec2 a_uv;
            layout(location = ${LOC_COLOR}) in vec4 a_color;

            uniform ivec2 u_targetSize;
            uniform int u_targetMipLevel;
            uniform mat3x2 u_worldTransform;
            uniform mat3x2 u_uvTransform;
            uniform mat3x2 u_targetViewTransform;
            uniform vec2 u_targetViewUvOffset;

            out vec2 v_worldPosition;
            out vec2 v_uv;
            out vec4 v_color;
            
            ${
                args.conservativeRasterization
                    ? `
            layout(location = ${LOC_FLAGS}) in float a_flags;
            layout(location = ${LOC_POSITION_PREV}) in vec2 a_positionPrev;
            layout(location = ${LOC_POSITION_NEXT}) in vec2 a_positionNext;
            layout(location = ${LOC_UV_GRAD}) in vec4 a_uvGrad;
            
            uniform float u_conservativeRasterizationPixelSize;
            uniform int u_conservativeRasterizationAddressMode;
            
            float bipolarSign(float x) {
                return x < 0.0 ? -1.0 : 1.0;
            }
            
            vec2 perpCW(vec2 v) {
                return vec2(-v.y, v.x);
            }
            
            void dilatePosition(float dilationAmount, vec2 positionPrev, vec2 position, vec2 positionNext, int localIndex, out vec2 dilatedDeltaPosition) {
                vec2 edgeNormalPrev = perpCW(position - positionPrev);
                vec2 edgeNormalNext = perpCW(positionNext - position);
                vec2 semiDiagonal0 = vec2(bipolarSign(edgeNormalPrev.x), bipolarSign(edgeNormalPrev.y));
                vec2 semiDiagonal1 = vec2(bipolarSign(edgeNormalNext.x), bipolarSign(edgeNormalNext.y));
                float dp = dot(semiDiagonal0, semiDiagonal1);
                vec2 diag;
                if (dp > 0.1) {
                    // the normals are in the same quadrant -> one vertex
                    diag = semiDiagonal0;
                }
                else if (dp < -0.1) {
                    // The normals are in opposite quadrants-> three vertices
                    if (localIndex == 1) {
                        // special "quadrant between two quadrants case"
                        diag = vec2(-semiDiagonal0.x * semiDiagonal0.y * semiDiagonal1.x,
                                    -semiDiagonal0.y * semiDiagonal1.x * semiDiagonal1.y);
                    }
                    else {
                        diag = localIndex == 0 ? semiDiagonal0 : semiDiagonal1;
                    }
                } else {
                    // the normals are in neighboring quadrants -> two vertices
                    diag = localIndex == 0 ? semiDiagonal0 : semiDiagonal1;
                }
                dilatedDeltaPosition = diag * dilationAmount;
            }
            `
                    : ""
            }
            
                        
            void main() {
                vec2 worldPosition = u_worldTransform * vec3(a_position, 1);
                vec2 uv = a_uv;
                vec4 color = a_color;
                ${
                    args.conservativeRasterization
                        ? `
                int flags = int(round(a_flags));
                if ((flags & ${VERTEX_FLAG_IS_BOUNDARY}) != 0) {
                    // If the vertex is a boundary vertex and conservative rasterization is enabled, offset the position to dilate the boundary and correct the UV coordinates.
                    vec2 worldPositionPrev = u_worldTransform * vec3(a_positionPrev, 1);
                    vec2 worldPositionNext = u_worldTransform * vec3(a_positionNext, 1);
                    int localIndex = flags & 3;
                    vec2 dilatedDeltaPosition;
                    dilatePosition(u_conservativeRasterizationPixelSize * 0.5, worldPositionPrev, worldPosition, worldPositionNext, localIndex, dilatedDeltaPosition);
                    worldPosition += dilatedDeltaPosition;
                    // adjust uv coordinates based on the dilated delta position
                    // when using wrap mode, we offset the texture coordinates according to the uv gradient
                    // when using clamp mode, limit gradient to along the edge
                    if (u_conservativeRasterizationAddressMode == ${ADDRESS_MODE_CLAMP_TO_EDGE}) {
                        // TODO make this more accurate
                        dilatedDeltaPosition *= vec2(0, 0);
                        // switch (localIndex) {
                        //     case 0:
                        //         //dilatedDeltaPosition *= normalize(abs(worldPosition - worldPositionPrev));
                        //         if (a_position.x > 1500.0 && a_position.y > 1500.0) {
                        //             dilatedDeltaPosition.y = 0.0;
                        //         } else if (a_position.x > 1500.0 && a_position.y < 1500.0) {
                        //             dilatedDeltaPosition.x = 0.0;
                        //         }
                        //         break;
                        //     case 1:
                        //         //dilatedDeltaPosition *= normalize(abs(worldPositionNext - worldPositionPrev));
                        //         dilatedDeltaPosition = vec2(0.0, 0.0);
                        //         break;
                        //     case 2:
                        //         //dilatedDeltaPosition *= normalize(abs(worldPositionNext - worldPosition));
                        //         if (a_position.x > 1500.0 && a_position.y > 1500.0) {
                        //             dilatedDeltaPosition.x = 0.0;
                        //         } else if (a_position.x > 1500.0 && a_position.y < 1500.0) {
                        //             dilatedDeltaPosition.y = 0.0;
                        //         }
                        //         break;
                        // }
                    }
                    float mipLevelScale = float(1 << u_targetMipLevel);
                    uv += (a_uvGrad.xy * dilatedDeltaPosition.x + a_uvGrad.zw * dilatedDeltaPosition.y) * mipLevelScale;
                }
                `
                        : ""
                }
                v_worldPosition = worldPosition;
                v_uv = u_uvTransform * vec3(uv, 1) + u_targetViewUvOffset;
                v_color = color;
                vec2 viewPosition = u_targetViewTransform * vec3(v_worldPosition, 1);
                gl_Position = vec4(viewPosition, 0.0, 1.0);
            }`
    }

    static genFragmentShader(args: {shadingFunction: string; returnType: ShaderReturnType}): string {
        const usedTextureUnits = WebGl2ShaderCodeGen.detectUsedTextureUnits(args.shadingFunction)
        let code = WebGl2ShaderCodeGen.genFragmentHeader(args.returnType)
        if (usedTextureUnits.length > 0) {
            code += WebGl2ShaderCodeGen.genFragmentTextureHeader()
        }
        // NOTE: We provide separate functions for each texture unit, because if we make the index a parameter we will get "ERROR: array index for samplers must be constant integral expressions" for "texture(u_image[index], ...)" even when it is a constant from the caller's point of view. :(
        for (let textureUnit of usedTextureUnits) {
            code += WebGl2ShaderCodeGen.genFragmentTextureUnitAccess(textureUnit)
        }
        code += args.shadingFunction
        code += WebGl2ShaderCodeGen.genFragmentMain(args.returnType)
        return code
    }

    private static detectUsedTextureUnits(shadingFunction: string): number[] {
        // instead of actually parsing we do some crude approximation by simply searching for texture access calls in the shading function
        const textureAccessFunctionNames = [
            "texelChannelFetchLod",
            "texelFetchLod",
            "texelFetch",
            "textureLodPx",
            "texturePx",
            "textureUv",
            "texelFetchPageTable",
        ]
        const regex = new RegExp(`\\b(${textureAccessFunctionNames.join("|")})(\\d+)\\b`, "g")
        const results = new Set<number>()
        let match: RegExpExecArray | null
        while ((match = regex.exec(shadingFunction)) !== null) {
            results.add(parseInt(match[2], 10))
        }
        return Array.from(results)
    }

    private static genFragmentHeader(returnType: ShaderReturnType): string {
        return `#version 300 es
            
            precision highp float;
            precision highp int;
            precision highp sampler2DArray;
            precision highp usampler2DArray;
            
            layout(location = 0) out ${returnType} color;
            
            uniform ivec2 u_targetSize;
            uniform vec4 u_modulationColor;
            uniform int u_clearAlpha;

            in vec2 v_worldPosition;
            in vec2 v_uv;
            in vec4 v_color;
        `
    }

    private static genFragmentTextureHeader(): string {
        // this is done because Android struggles with arrays of samplers apparently :/
        let imageSamplerDefinitions = ""
        let atlasSamplerDefinitions = ""
        for (let i = 0; i < MAX_TEXTURE_UNITS; i++) {
            imageSamplerDefinitions += `uniform sampler2DArray u_image${i};\n`
        }
        for (let j = 0; j < MAX_ATLAS_COUNT; j++) {
            atlasSamplerDefinitions += `uniform sampler2DArray u_atlas${j};\n`
        }
        return `
            ${imageSamplerDefinitions}
            ${atlasSamplerDefinitions}
            //uniform sampler2DArray u_image[${MAX_TEXTURE_UNITS}];

            ${
                ALLOW_WRAPPING_VIEWS
                    ? `
            uniform ivec2 u_imageWrapOffset[${MAX_TEXTURE_UNITS}];
            uniform ivec2 u_imageWrapSize[${MAX_TEXTURE_UNITS}];
            uniform ivec2 u_imageWrapGap[${MAX_TEXTURE_UNITS}];
            `
                    : ""
            }

            uniform ivec2 u_viewSize[${MAX_TEXTURE_UNITS}];
            uniform ivec2 u_viewOffset[${MAX_TEXTURE_UNITS}];
            uniform int u_viewMipLevelOffset[${MAX_TEXTURE_UNITS}];

            uniform ivec2 u_shardSize[${MAX_TEXTURE_UNITS}];
            uniform ivec2 u_numShards[${MAX_TEXTURE_UNITS}];
            uniform int u_numChannels[${MAX_TEXTURE_UNITS}];
            uniform int u_numMipLevels[${MAX_TEXTURE_UNITS}];
            uniform int u_atlasIndex[${MAX_TEXTURE_UNITS}];

            uniform ivec2 u_atlasShardSize;
            uniform ivec2 u_atlasNumShards;

            uniform ivec4 u_pageLookupMipLevelInfo[${MAX_TEXTURE_UNITS} * ${MAX_MIP_LEVELS}];   // x: first page index, y: num mip level pages x, zw: base data offset
            uniform int u_pageLookupWidth[${MAX_TEXTURE_UNITS}];
            
            const int ADDRESS_MODE_CLAMP_TO_EDGE = ${ADDRESS_MODE_CLAMP_TO_EDGE};
            const int ADDRESS_MODE_REPEAT = ${ADDRESS_MODE_REPEAT};
            const int ADDRESS_MODE_MIRRORED_REPEAT = ${ADDRESS_MODE_MIRRORED_REPEAT};
            const int ADDRESS_MODE_BORDER = ${ADDRESS_MODE_BORDER};
            
            float wrapFloat(float value, float maxValue) {
                value = mod(value, maxValue);
                return value >= 0.0 ? value : value + maxValue;
            }

            vec2 wrapTexelIndex(int index, vec2 texelIndex) {
                vec2 imageSize = vec2(u_viewSize[index]);
                texelIndex.x = wrapFloat(texelIndex.x, imageSize.x);
                texelIndex.y = wrapFloat(texelIndex.y, imageSize.y);
                return texelIndex;
            }

            int wrapInt(int value, int maxValue) {
                value = value % maxValue;
                return value >= 0 ? value : value + maxValue;
            }

            ivec2 wrapTexelIndex(int index, ivec2 texelIndex) {
                ivec2 imageSize = u_viewSize[index];
                texelIndex.x = wrapInt(texelIndex.x, imageSize.x);
                texelIndex.y = wrapInt(texelIndex.y, imageSize.y);
                return texelIndex;
            }
            
            // returns false if the page table texel index is invalid
            // bool decodePageTableTexelIndex(int index, int lod, ivec2 pageTableTexelIndex, out ivec2 outPageIndex, out int outChannel) {
            //     ivec4 pageLookupMipLevelInfo = u_pageLookupMipLevelInfo[index * ${MAX_MIP_LEVELS} + lod];
            //     int firstPageIndex = pageLookupMipLevelInfo.x;
            //     int numMipLevelPagesX = pageLookupMipLevelInfo.y;
            //     int numMipLevelPagesY = pageLookupMipLevelInfo.z;
            //     int pageLookupWidth = u_pageLookupWidth[index];
            //     int absolutePageIndex = pageTableTexelIndex.y * pageLookupWidth + pageTableTexelIndex.x;
            //     int relativePageIndex = absolutePageIndex - firstPageIndex;
            //     if (relativePageIndex > numMipLevelPagesX * numMipLevelPagesY) {
            //         return false;
            //     }
            //     outPageIndex = ivec2(relativePageIndex % numMipLevelPagesX, relativePageIndex / numMipLevelPagesX);
            //     outChannel = 0; // TODO
            // }
            
            void getPageLookupMipLevelInfo(int index, int lod, out int outFirstPageIndex, out ivec2 outNumMipLevelPages) {
                ivec4 pageLookupMipLevelInfo = u_pageLookupMipLevelInfo[index * ${MAX_MIP_LEVELS} + lod];
                outFirstPageIndex = pageLookupMipLevelInfo.x;
                outNumMipLevelPages = ivec2(pageLookupMipLevelInfo.y, pageLookupMipLevelInfo.z);
            }
            
            ivec2 computePageTableTexelIndex(int index, ivec2 pageIndex, int channel, int lod) {
                int firstPageIndex;
                ivec2 numMipLevelPages;
                getPageLookupMipLevelInfo(index, lod, firstPageIndex, numMipLevelPages);
                int numChannels = u_numChannels[index];
                int relativePageIndex = pageIndex.y * numMipLevelPages.x + pageIndex.x;
                int linearPageLookupIndex = (firstPageIndex + relativePageIndex) * numChannels + channel;
                int pageLookupWidth = u_pageLookupWidth[index];
                ivec2 pageLookupTexelIndex = ivec2(linearPageLookupIndex % pageLookupWidth, linearPageLookupIndex / pageLookupWidth);
                return pageLookupTexelIndex;
            }     
            
            ivec3 computePhysicalIndex(int index, ivec2 texelIndex, int layer) {
                ivec2 shardTexel = texelIndex % u_shardSize[index];
                ivec2 shardIndex2 = texelIndex / u_shardSize[index];
                int shardIndex = shardIndex2.y * u_numShards[index].x + shardIndex2.x;
                int layerIndex = layer * u_numShards[index].x * u_numShards[index].y + shardIndex;
                return ivec3(shardTexel, layerIndex);
            }
                        
            ivec2 getViewSizeLod(int index, int lod) {
                return ivec2(ceil(vec2(u_viewSize[index]) / float(1 << lod)));
            }
            
            ${
                ALLOW_WRAPPING_VIEWS
                    ? `
            ivec2 getWrapOffsetLod(int index, int lod) {
                return ivec2(ceil(vec2(u_imageWrapOffset[index]) / float(1 << lod)));
            }
            
            ivec2 getWrapSizeLod(int index, int lod) {
                return ivec2(ceil(vec2(u_imageWrapSize[index]) / float(1 << lod)));
            }
            
            ivec2 getWrapGapLod(int index, int lod) {
                return ivec2(ceil(vec2(u_imageWrapGap[index]) / float(1 << lod)));
            }
            `
                    : ""
            }
            
            bool isBorderTexelLod(int index, ivec2 texelIndex, int lod, int addressMode) {
                switch (addressMode) {
                    case ADDRESS_MODE_BORDER:
                        ivec2 imageSize = getViewSizeLod(index, lod);
                        return texelIndex.x < 0 
                            || texelIndex.y < 0
                            || texelIndex.x >= imageSize.x 
                            || texelIndex.y >= imageSize.y;
                    default:
                        return false;
                }
            }
            
            bool isBorderTexelLod(int index, ivec2 texelIndex, int lod) {
                return isBorderTexelLod(index, texelIndex, lod, ADDRESS_MODE_BORDER);
            }
            
            bool isBorderTexel(int index, vec2 texelIndex, int addressMode) {
                switch (addressMode) {
                    case ADDRESS_MODE_BORDER:
                        return texelIndex.x < 0.0 
                            || texelIndex.y < 0.0
                            || texelIndex.x >= float(u_viewSize[index].x) 
                            || texelIndex.y >= float(u_viewSize[index].y);
                    default:
                        return false;
                }
            }

            bool isBorderTexel(int index, ivec2 texelIndex, int addressMode) {
                switch (addressMode) {
                    case ADDRESS_MODE_BORDER:
                        return texelIndex.x < 0 
                            || texelIndex.y < 0
                            || texelIndex.x >= u_viewSize[index].x 
                            || texelIndex.y >= u_viewSize[index].y;
                    default:
                        return false;
                }
            }
            
            bool isBorderTexel(int index, ivec2 texelIndex) {
                return isBorderTexel(index, texelIndex, ADDRESS_MODE_BORDER);
            }
            
            vec2 applyAddressMode(int index, vec2 texelIndex, int addressMode) {
                ivec2 imageSize = u_viewSize[index];
                switch (addressMode) {
                    case ADDRESS_MODE_CLAMP_TO_EDGE:
                    case ADDRESS_MODE_BORDER:
                        return clamp(texelIndex, vec2(0), vec2(imageSize - 1));
                    case ADDRESS_MODE_MIRRORED_REPEAT:
                        if (texelIndex.x < 0.0) texelIndex.x += 1.0;
                        if (texelIndex.y < 0.0) texelIndex.y += 1.0;
                        texelIndex = abs(texelIndex);
                        texelIndex = vec2(mod(vec2(texelIndex), vec2(imageSize) * 2.0));
                        if (texelIndex.x >= float(imageSize.x)) 
                            texelIndex.x = float(imageSize.x) * 2.0 - 1.0 - texelIndex.x;
                        if (texelIndex.y >= float(imageSize.y))
                            texelIndex.y = float(imageSize.y) * 2.0 - 1.0 - texelIndex.y;
                        return texelIndex;
                    case ADDRESS_MODE_REPEAT:
                    default:
                        return wrapTexelIndex(index, texelIndex);
                }
            }

            ivec2 applyAddressMode(int index, ivec2 texelIndex, int addressMode) {
                ivec2 imageSize = u_viewSize[index];
                switch (addressMode) {
                    case ADDRESS_MODE_CLAMP_TO_EDGE:
                    case ADDRESS_MODE_BORDER:
                        return clamp(texelIndex, ivec2(0), imageSize - 1);
                    case ADDRESS_MODE_MIRRORED_REPEAT: {
                        // NOTE: we move texelIndex into individual components to do the conditional increment, because of a bug in Chrome's GLSL Metal compiler
                        int texelIndexX = texelIndex.x;
                        int texelIndexY = texelIndex.y;
                        if (texelIndexX < 0) texelIndexX++;
                        if (texelIndexY < 0) texelIndexY++;
                        texelIndex = ivec2(texelIndexX, texelIndexY);
                        
                        texelIndex = abs(texelIndex);
                        texelIndex = ivec2(mod(vec2(texelIndex), vec2(imageSize) * 2.0));
                        if (texelIndex.x >= imageSize.x) 
                            texelIndex.x = imageSize.x * 2 - 1 - texelIndex.x;
                        if (texelIndex.y >= imageSize.y)
                            texelIndex.y = imageSize.y * 2 - 1 - texelIndex.y;
                        return texelIndex;
                    }
                    case ADDRESS_MODE_REPEAT:
                    default:
                        return wrapTexelIndex(index, texelIndex);
                }
            }
                        
            ivec2 applyAddressModeLod(int index, ivec2 texelIndex, ivec2 viewSize, int addressMode) {
                switch (addressMode) {
                    case ADDRESS_MODE_CLAMP_TO_EDGE:
                    case ADDRESS_MODE_BORDER:
                        return clamp(texelIndex, ivec2(0), viewSize - 1);
                    case ADDRESS_MODE_MIRRORED_REPEAT: {
                        // NOTE: we move texelIndex into individual components to do the conditional increment, because of a bug in Chrome's GLSL Metal compiler
                        int texelIndexX = texelIndex.x;
                        int texelIndexY = texelIndex.y;
                        if (texelIndexX < 0) texelIndexX++;
                        if (texelIndexY < 0) texelIndexY++;
                        texelIndex = ivec2(texelIndexX, texelIndexY);

                        texelIndex = abs(texelIndex);
                        texelIndex = ivec2(mod(vec2(texelIndex), vec2(viewSize) * 2.0));
                        if (texelIndex.x >= viewSize.x) 
                            texelIndex.x = viewSize.x * 2 - 1 - texelIndex.x;
                        if (texelIndex.y >= viewSize.y)
                            texelIndex.y = viewSize.y * 2 - 1 - texelIndex.y;
                        return texelIndex;
                    }
                    case ADDRESS_MODE_REPEAT:
                    default:
                        texelIndex.x = wrapInt(texelIndex.x, viewSize.x);
                        texelIndex.y = wrapInt(texelIndex.y, viewSize.y);
                        return texelIndex;
                }
            }
            
            // the view's lod that is actually sampled when lod is requested (see getAbsoluteTexelIndex)
            int getActualViewLod(int index, int lod) {
                int viewMipLevelOffset = u_viewMipLevelOffset[index];
                int actualSourceLod = clamp(lod + viewMipLevelOffset, 0, u_numMipLevels[index] - 1);
                return actualSourceLod - viewMipLevelOffset;
            }
            
            // Folds a continuous texel index into the first period of the address mode (repeat: [0, size), mirrored repeat: [0, size]) and leaves it untouched for the non-periodic modes.
            // This happens at the exact size of the lod (size / 2^lod), not the ceil'd one the integer texel fetches wrap at, because the latter would shift coarse lods against finer ones
            // for sizes not divisible by 2^lod. It is only a pre-pass for interpolation: the taps around the folded index can still lie one texel outside the lod, which the fetch resolves.
            vec2 foldIntoFirstPeriodLod(int index, vec2 texelIndex, int lod, int addressMode) {
                vec2 lodSize = vec2(u_viewSize[index]) / float(1 << getActualViewLod(index, lod));
                switch (addressMode) {
                    case ADDRESS_MODE_REPEAT:
                        return mod(texelIndex, lodSize);
                    case ADDRESS_MODE_MIRRORED_REPEAT: {
                        vec2 wrapped = mod(texelIndex, lodSize * 2.0);
                        return mix(wrapped, lodSize * 2.0 - wrapped, greaterThanEqual(wrapped, lodSize));
                    }
                    default:
                        return texelIndex;
                }
            }
            
            // Besides the absolute texel index, also outputs the actual (clamped-to-available) source lod -
            // callers must sample at THIS lod, not the requested one, since every coordinate above (view
            // size, address-mode wrapping/clamping, view offset) was computed relative to it.
            bool getAbsoluteTexelIndex(int index, ivec2 texelIndex, int lod, int addressMode, out ivec2 outAbsoluteTexelIndex, out int outActualLod) {
                // u_numMipLevels[index] counts the real, physical source texture's mip levels (its mip chain
                // may be shorter than the lod being requested - e.g. a 1x1 physical image only ever has mip
                // 0), so the available-lod check has to happen in that space (lod + the view's own mip
                // bias), while view size/offset/address-mode below are all relative to the view's OWN mip 0
                // - so the clamped result gets the bias subtracted back out before use.
                int viewMipLevelOffset = u_viewMipLevelOffset[index];
                int actualViewLod = getActualViewLod(index, lod);
                int actualSourceLod = actualViewLod + viewMipLevelOffset;
                if (isBorderTexelLod(index, texelIndex, actualViewLod, addressMode)) {
                    return false;
                }
                ivec2 viewSize = getViewSizeLod(index, actualViewLod);
                outAbsoluteTexelIndex = applyAddressModeLod(index, texelIndex, viewSize, addressMode); // clamp or wrap the texel index to the view size
                outAbsoluteTexelIndex += u_viewOffset[index] >> actualViewLod;   // apply view offset
                ${
                    ALLOW_WRAPPING_VIEWS
                        ? `
                // wrap the image-space texel index to support wrapping views
                ivec2 wrapOffset = getWrapOffsetLod(index, actualViewLod);
                ivec2 wrapSize = getWrapSizeLod(index, actualViewLod);
                ivec2 wrapGap = getWrapGapLod(index, actualViewLod);
                outAbsoluteTexelIndex -= wrapOffset;
                outAbsoluteTexelIndex = ivec2(wrapInt(outAbsoluteTexelIndex.x, wrapSize.x + wrapGap.x),
                                              wrapInt(outAbsoluteTexelIndex.y, wrapSize.y + wrapGap.y));
                if (outAbsoluteTexelIndex.x >= wrapSize.x || outAbsoluteTexelIndex.y >= wrapSize.y) {
                    return false; // texel is in the gap area
                }
                outAbsoluteTexelIndex += wrapOffset;
                `
                        : ""
                }
                outActualLod = actualSourceLod;
                return true;
            }

            vec4 fillMissingChannels(int index, vec4 color) {
                switch (u_numChannels[index]) {
                    case 1:
                        return vec4(color.r, color.r, color.r, 1);
                    case 2:
                        return vec4(color.r, color.g, 0, 1);
                    case 3:
                        return vec4(color.r, color.g, color.b, 1);
                    case 4:
                    default:
                        return color;
                }
            }

            ivec3 computeAtlasPhysicalIndex(ivec2 texelIndex) {
                ivec2 shardTexel = texelIndex % u_atlasShardSize;
                ivec2 shardIndex2 = texelIndex / u_atlasShardSize;
                int shardIndex = shardIndex2.y * u_atlasNumShards.x + shardIndex2.x;
                return ivec3(shardTexel, shardIndex);
            }

            float lowLevelFetchAtlas(int atlasIndex, ivec2 texelIndex) {
                ivec3 physicalIndex = computeAtlasPhysicalIndex(texelIndex);
                if (atlasIndex == 0) return texelFetch(u_atlas0, physicalIndex, 0).r;
                else if (atlasIndex == 1) return texelFetch(u_atlas1, physicalIndex, 0).r;
                else if (atlasIndex == 2) return texelFetch(u_atlas2, physicalIndex, 0).r;
                else if (atlasIndex == 3) return texelFetch(u_atlas3, physicalIndex, 0).r;
                else if (atlasIndex == 4) return texelFetch(u_atlas4, physicalIndex, 0).r;
                else if (atlasIndex == 5) return texelFetch(u_atlas5, physicalIndex, 0).r;
                else if (atlasIndex == 6) return texelFetch(u_atlas6, physicalIndex, 0).r;
                else if (atlasIndex == 7) return texelFetch(u_atlas7, physicalIndex, 0).r;
                return 0.0;
            }
        `
    }

    private static genFragmentTextureUnitAccess(index: number): string {
        return `
            uvec4 texelFetchPageTable${index}(ivec2 texelIndex) {
                ivec3 physicalIndex = computePhysicalIndex(${index}, texelIndex, 0);
                uint packedData = floatBitsToUint(texelFetch(u_image${index}, physicalIndex, 0).r);
                uint atlasX = packedData & 0xFFu;
                uint atlasY = (packedData >> 8u) & 0xFFu;
                uint coarsenedLevels = (packedData >> 16u) & 0xFu;
                uint isValid = packedData >> 31u;
                return uvec4(atlasX, atlasY, coarsenedLevels, isValid);
            }

            int getPageTableAtlasIndex${index}(ivec2 texelIndex) {
                ivec3 physicalIndex = computePhysicalIndex(${index}, texelIndex, 0);
                uint packedData = floatBitsToUint(texelFetch(u_image${index}, physicalIndex, 0).r);
                return int((packedData >> 20u) & 0x7u);
            }

            uvec4 texelFetchPageTable${index}(ivec2 pageIndex, int channel, int mipLevel) {
                ivec2 pageTableTexelIndex = computePageTableTexelIndex(${index}, pageIndex, channel, mipLevel);
                uvec4 pageTableData = texelFetchPageTable${index}(pageTableTexelIndex);
                return pageTableData;
            }
            
            // lod must already be clamped to an available mip level (see getAbsoluteTexelIndex) - callers reach this only via texelFetchLod/texelChannelFetchLod, which guarantee that.
            vec4 lowLevelTexelFetchLodPhysical${index}(ivec2 texelIndex, int layer, int lod) {
                ivec3 physicalIndex = computePhysicalIndex(${index}, texelIndex, layer);
                vec4 color = texelFetch(u_image${index}, physicalIndex, lod);
                return fillMissingChannels(${index}, color);
            }
            
            float lowLevelTexelChannelFetchLodPhysical${index}(ivec2 texelIndex, int lod, int channel) {
                if (channel >= u_numChannels[${index}]) {
                    return 0.0;
                }
                vec4 color = lowLevelTexelFetchLodPhysical${index}(texelIndex, 0, lod);
                switch (channel) {
                    case 0: return color.r;
                    case 1: return color.g;
                    case 2: return color.b;
                    case 3: return color.a;
                    default: return 0.0;
                }
            }
            
            // lod must already be clamped to an available mip level (see getAbsoluteTexelIndex) - callers reach this only via texelFetchLod/texelChannelFetchLod, which guarantee that.
            float lowLevelTexelChannelFetchLodVirtual${index}(ivec2 texelIndex, int lod, int channel) {
                if (channel >= u_numChannels[${index}]) {
                    return 0.0;
                }
                const int VT_PAGE_SIZE_SHIFT = ${WebGl2ImageAtlas.pageSizeShift};
                const int VT_PAGE_MARGIN = ${WebGl2ImageAtlas.pageMargin};
                ivec2 pageIndex = texelIndex >> VT_PAGE_SIZE_SHIFT;
                ivec2 pageTableTexelIndex = computePageTableTexelIndex(${index}, pageIndex, channel, lod);
                uvec4 pageTableData = texelFetchPageTable${index}(pageTableTexelIndex);
                if (pageTableData.w != 0u) {
                    // the resident page may be coarser than requested if propagateCoarserMipLevelsToMissingPages substituted it in at eviction time
                    int mipLevelOfs = int(pageTableData.z);
                    if (mipLevelOfs < 0) {
                        texelIndex <<= -mipLevelOfs;
                    } else {
                        texelIndex >>= mipLevelOfs;
                    }
                    ivec2 pageTexelIndex = texelIndex & ((1 << VT_PAGE_SIZE_SHIFT) - 1);
                    ivec2 atlasTexelIndex = ivec2(ivec2(pageTableData.xy) * ((1 << VT_PAGE_SIZE_SHIFT) + VT_PAGE_MARGIN) + pageTexelIndex);
                    int atlasIdx = getPageTableAtlasIndex${index}(pageTableTexelIndex);
                    return lowLevelFetchAtlas(atlasIdx, atlasTexelIndex);
                }
                return 0.0;
            }
            
            float texelChannelFetchLod${index}(ivec2 texelIndex, int lod, int channel, int addressMode, float borderColor) {
                int actualLod;
                if (!getAbsoluteTexelIndex(${index}, texelIndex, lod, addressMode, texelIndex, actualLod)) {
                    return borderColor;
                }
                int atlasIndex = u_atlasIndex[${index}];
                if (atlasIndex != -1) {
                    return lowLevelTexelChannelFetchLodVirtual${index}(texelIndex, actualLod, channel);
                }
                else {
                    return lowLevelTexelChannelFetchLodPhysical${index}(texelIndex, actualLod, channel);
                }
            }
            
            float texelChannelFetchLod${index}(ivec2 texelIndex, int lod, int channel) {
                return texelChannelFetchLod${index}(texelIndex, lod, channel, ADDRESS_MODE_BORDER, 0.0);
            }
                        
            vec4 texelFetchLod${index}(ivec2 texelIndex, int lod, int addressMode, vec4 borderColor) {
                int actualLod;
                if (!getAbsoluteTexelIndex(${index}, texelIndex, lod, addressMode, texelIndex, actualLod)) {
                    return borderColor;
                }
                int atlasIndex = u_atlasIndex[${index}];
                if (atlasIndex != -1) {
                    float r = lowLevelTexelChannelFetchLodVirtual${index}(texelIndex, actualLod, 0);
                    float g = lowLevelTexelChannelFetchLodVirtual${index}(texelIndex, actualLod, 1);
                    float b = lowLevelTexelChannelFetchLodVirtual${index}(texelIndex, actualLod, 2);
                    float a = lowLevelTexelChannelFetchLodVirtual${index}(texelIndex, actualLod, 3);
                    vec4 color = vec4(r, g, b, a);
                    return fillMissingChannels(${index}, color);
                }
                else {
                    return lowLevelTexelFetchLodPhysical${index}(texelIndex, 0, actualLod);
                }
            }
            
            vec4 texelFetchLod${index}(ivec2 texelIndex, int lod, int addressMode) {
                return texelFetchLod${index}(texelIndex, lod, addressMode, vec4(0, 0, 0, 0));
            }
            
            vec4 texelFetchLod${index}(ivec2 texelIndex, int lod) {
                return texelFetchLod${index}(texelIndex, lod, ADDRESS_MODE_BORDER); // TODO should this be ADDRESS_MODE_REPEAT also or at least consistent with texelFetch ?
            }

            vec4 texelFetch${index}(ivec2 texelIndex, int addressMode, vec4 borderColor) {
                return texelFetchLod${index}(texelIndex, 0, addressMode, borderColor);
            }

            vec4 texelFetch${index}(ivec2 texelIndex, int addressMode) {
                return texelFetch${index}(texelIndex, addressMode, vec4(0, 0, 0, 0));
            }

            vec4 texelFetch${index}(ivec2 texelIndex) {
                return texelFetch${index}(texelIndex, ADDRESS_MODE_REPEAT);
            }
            
            // samples a texture with interpolation (bilinear filtering); single texel sampling happens at the center of the texel (0.5, 0.5)
            vec4 textureLodPx${index}(vec2 texelIndex, int lod, int addressMode, vec4 borderColor) {
                texelIndex = foldIntoFirstPeriodLod(${index}, texelIndex, lod, addressMode); // the taps below are then resolved by the texel fetches
                texelIndex -= 0.5;  // we offset by 0.5 to sample at the center of the texel instead of the corner
                ivec2 texelIndexI = ivec2(floor(texelIndex));
                vec2 texelIndexF = texelIndex - vec2(texelIndexI);
                vec4 texColor00 = texelFetchLod${index}(texelIndexI + ivec2(0, 0), lod, addressMode, borderColor);
                vec4 texColor10 = texelFetchLod${index}(texelIndexI + ivec2(1, 0), lod, addressMode, borderColor);
                vec4 texColor01 = texelFetchLod${index}(texelIndexI + ivec2(0, 1), lod, addressMode, borderColor);
                vec4 texColor11 = texelFetchLod${index}(texelIndexI + ivec2(1, 1), lod, addressMode, borderColor);
                vec4 texColor = mix(mix(texColor00, texColor10, texelIndexF.x), mix(texColor01, texColor11, texelIndexF.x), texelIndexF.y);
                return texColor;
            }
            
            vec4 textureLodPx${index}(vec2 texelIndex, int lod, int addressMode) {
                return textureLodPx${index}(texelIndex, lod, addressMode, vec4(0, 0, 0, 0));
            }
            
            vec4 textureLodPx${index}(vec2 texelIndex, int lod) {
                return textureLodPx${index}(texelIndex, lod, ADDRESS_MODE_BORDER);
            }
            
            vec4 texturePx${index}(vec2 texelIndex, int addressMode, vec4 borderColor) {
                return textureLodPx${index}(texelIndex, 0, addressMode, borderColor);
            }
            
            vec4 texturePx${index}(vec2 texelIndex, int addressMode) {
                return texturePx${index}(texelIndex, addressMode, vec4(0, 0, 0, 0));
            }
            
            vec4 texturePx${index}(vec2 texelIndex) {
                return texturePx${index}(texelIndex, ADDRESS_MODE_BORDER);
            }
            
            vec4 textureUv${index}(vec2 uv, int addressMode) {
                vec2 texelIndex = uv * vec2(u_viewSize[${index}]);
                return texturePx${index}(texelIndex, addressMode);
            }

            vec4 textureUv${index}(vec2 uv) {
                return textureUv${index}(uv, ADDRESS_MODE_CLAMP_TO_EDGE);
            }
        `
    }

    private static genFragmentMain(returnType: ShaderReturnType): string {
        return `
            void main() {
                ${returnType} computedColor = computeColor(v_worldPosition, v_uv, v_color);
                color = computedColor${
                    returnType === "vec4"
                        ? `
                 * u_modulationColor;
                if (u_clearAlpha != 0) {
                    color.a = 1.0;
                }`
                        : ""
                };
            }
        `
    }
}
